/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { DropdownMenuItemType, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Separator } from '@fluentui/react/lib/Separator';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextFieldProps } from '@fluentui/react/lib/TextField';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton, CalloutButtonProps } from 'src/core/components/CalloutButton';
import { DragAndDropInput } from 'src/core/components/DragAndDropInput';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { fileService } from 'src/core/services/api/fileService';
import {
    logDragAndDropFiles,
    logFeaturesResouceSelection,
    logFeaturesTextLanguage,
    logSampleClick
} from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountKind, AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { CultureStore } from 'src/core/stores/CultureStore';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { noop } from 'src/core/utils/base';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ApiAndModelVersionsModal } from 'src/languageStudio/appShell/prebuiltDemos/components/ApiAndModelVersionsModal';
import { SampleButton } from 'src/languageStudio/appShell/prebuiltDemos/components/SampleButton';
import { labelDescriptionIconClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { DemoSample } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';
import {
    ResourceValidationResult,
    validateLanguageStudioResource,
    validateTranslatorResource
} from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import styled from 'styled-components';

export type DocumentInputStepProps = {
    document: string;
    isTextRTL?: boolean;
    isPaidDemo?: boolean;
    documentLimit?: number;
    displayTitle?: boolean;
    featureName?: TelemetryFeatureTitle;
    samplesList: DemoSample[];
    children?: React.ReactNode;
    selectedSampleTitle?: string;
    supportedRegions?: Region[];
    hideFilesUploader?: boolean;
    supportedRegionLink?: string;
    /**
     * Add learn more link in versions modal if provided
     */
    versionsLearnMoreLink?: string;
    onInputClearClick?: () => void;
    resourceDropdownLabel?: string;
    textFieldProps?: ITextFieldProps;
    containerStackProps?: IStackProps;
    disableLanguageSelection?: boolean;
    /**
     * Show model version drop down list if false
     */
    disableModelVersionSelection?: boolean;
    /**
     * Show Modal that contains versions drop down list if false
     */
    disableVersionsModalSelection?: boolean;
    displayAzureResourcePicker?: boolean;
    selectedLanguage?: CultureStore['code'];
    selectedModelVersion?: TryoutModelVersion;
    selectedApiVersion?: TryoutModelVersion;
    /**
     * Replace model Version Options if provided
     */
    supportedModelVersionOptions?: IDropdownOption[];
    supportedApiVersionOptions?: IDropdownOption[];
    onModelVersionChange?: (newVersion: TryoutModelVersion) => void;
    onApiVersionChange?: (newVersion: TryoutModelVersion) => void;
    acceptedResourceType?: AzureCsAccountKind;
    onDocumentChange: (newDoc: string) => void;
    onSampleClick?: (sample: DemoSample) => void;
    languagesList?: Pick<CultureStore, 'code' | 'name'>[];
    onLanguageAutoDetectionClick?: (checked: boolean) => void;
    onLanguageChange?: (newLanguage: Language['code']) => void;
    onRenderHorizontalOptions?: (disabled?: boolean) => React.ReactNode;
    filterResourcesPredicate?: (resource: AzureCsAccountStore) => boolean;
    onRenderOptions?: (defaultRenderer: () => React.ReactNode) => React.ReactNode;
};

const messages = defineMessages({
    model: { id: 'languageStudio.demoPage.model', defaultMessage: 'Model' },
    preview: { id: 'languageStudio.demoPage.preview', defaultMessage: 'Preview' },
    apiVersion: { id: 'languageStudio.demoPage.apiVersion', defaultMessage: 'API Version' },
    clearText: { id: 'languageStudio.demoPage.clearText', defaultMessage: 'Clear text box' },
    modelVersion: { id: 'languageStudio.demoPage.modelVersion', defaultMessage: 'Model Version' },
    goToSettings: { id: 'languageStudio.demoPage.goToSettings', defaultMessage: 'Go to Settings' },
    changeVersions: { id: 'languageStudio.demoPage.changeVersions', defaultMessage: 'Change versions' },
    generallyAvailable: { id: 'languageStudio.demoPage.generallyAvailable', defaultMessage: 'Generally Available' },
    sourceTextPlaceholder: { id: 'languageStudio.demoPage.sourceTextPlaceholder', defaultMessage: 'Enter text here' },
    selectLanguageLabel: { id: 'languageStudio.demoPage.selectLanguageLabel', defaultMessage: 'Select text language' },
    selectModelVersionLabel: { id: 'languageStudio.demoPage.selectModelVersionLabel', defaultMessage: 'Select Model version' },
    standardResourcesTitle: { id: 'languageStudio.demoPage.standardResourcesTitle', defaultMessage: 'Standard tier (S)' },
    enterTextStepTitle: { id: 'languageStudio.demoPage.enterTextStepTitle', defaultMessage: 'Enter some text to try out' },
    fZeroResourcesTitle: { id: 'languageStudio.demoPage.fZeroResourcesTitle', defaultMessage: 'Free tier (F0) recommended' },
    autoDetectLanguageItem: { id: 'languageStudio.demoPage.autoDetectLanguageItem', defaultMessage: 'Auto-detect language' },
    selectLanguagePlaceholder: { id: 'languageStudio.demoPage.selectLanguagePlaceholder', defaultMessage: 'Select a language' },
    selectResourcePlaceholder: { id: 'languageStudio.demoPage.selectResourcePlaceholder', defaultMessage: 'Select a language resource' },
    selectModelVersionPlaceholder: { id: 'languageStudio.demoPage.selectModelVersionPlaceholder', defaultMessage: 'Select model version' },
    selectResourceLabel: {
        id: 'languageStudio.demoPage.selectResourceLabel',
        defaultMessage: 'Select your Azure resource (Free tier (F0) recommended)'
    },
    enterSourceText: {
        id: 'languageStudio.demoPage.enterSourceText',
        defaultMessage: 'Enter your own text, upload a file, or use one of our sample texts'
    },
    signedOutEnterSourceText: {
        id: 'languageStudio.demoPage.signedOutEnterSourceText',
        defaultMessage: 'Use one of our sample texts below to try out the experience'
    },
    selectLanguageDescription: {
        id: 'languageStudio.demoPage.selectLanguageDescription',
        defaultMessage: `This is the language your text is in. If auto-detect language is enabled
        it may incur more costs See pricing details below.`
    },
    selectModelVersionDescription: {
        id: 'languageStudio.demoPage.selectModelVersionDescription',
        defaultMessage: `Switch between the latest model available in preview, or our latest generally available stable version.`
    },
    selectResourceDescription: {
        id: 'languageStudio.demoPage.selectResourceDescription',
        defaultMessage: `Running this tryout requires an Azure resource. We recommend you choose
        a Free tier (F0) resource or an Azure Free Account to minimize costs to you.`
    },
    uploadingLargeFilesNote: {
        id: 'languageStudio.demoPage.uploadingLargeFilesNote',
        defaultMessage: 'The file has a size larger than the maximum limit. It will be trimmed down.'
    }
});

const stepTheme = getScopedTheme('DemoPage');
const { styleComponent } = getStylist('DemoPage');
const actionButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { height: 'fit-content' } });

const SecondaryText = styleComponent<LuisTextProps>(LuisText)('SecondaryText', theme => ({
    color: theme.secondaryTextColor,
    marginTop: '5px'
}));
const StyledStack = styled(Stack)<{ isLargeOrGreater: boolean }>(props => ({
    alignItems: props.isLargeOrGreater ? 'end' : 'normal'
}));
const StyledStackText = styled(Stack)<{ isLargeOrGreater: boolean; haveHorizontalOptions: boolean }>(props => ({
    width: props.haveHorizontalOptions && props.isLargeOrGreater ? '70%' : '100%'
}));
const StyledDropdown = styled(LuisDropdown)<{ isLargeOrGreater: boolean }>(props => ({
    minWidth: props.isLargeOrGreater ? '300px' : '100%',
    maxWidth: props.isLargeOrGreater ? '300px' : '100%'
}));

const renderDescribedLabel = ({ label, descriptionIconProps }: { label: string; descriptionIconProps: CalloutButtonProps }) => (
    <Stack horizontal verticalAlign="center" tokens={{ padding: '0 0 5px 0' }}>
        <LuisText variant="medium" fontWeight={500}>
            {label}
        </LuisText>

        <CalloutButton {...descriptionIconProps} iconClassName={labelDescriptionIconClassName} />

        <LuisText variant="medium" fontWeight={500} color={stepTheme.requiredColor}>
            *
        </LuisText>
    </Stack>
);

export const DocumentInputStep = observer((props: DocumentInputStepProps) => {
    const {
        document,
        children,
        featureName,
        samplesList,
        onSampleClick,
        textFieldProps,
        onRenderOptions,
        selectedLanguage,
        selectedModelVersion,
        versionsLearnMoreLink,
        selectedApiVersion,
        onDocumentChange,
        supportedRegions,
        isTextRTL = false,
        onInputClearClick,
        isPaidDemo = false,
        languagesList = [],
        supportedRegionLink,
        selectedSampleTitle,
        containerStackProps,
        displayTitle = true,
        documentLimit = 5000,
        acceptedResourceType,
        resourceDropdownLabel,
        onLanguageChange = noop,
        onModelVersionChange = noop,
        onApiVersionChange = noop,
        onRenderHorizontalOptions,
        supportedModelVersionOptions,
        supportedApiVersionOptions,
        hideFilesUploader = false,
        onLanguageAutoDetectionClick,
        disableLanguageSelection = false,
        disableModelVersionSelection = true,
        disableVersionsModalSelection = true,
        displayAzureResourcePicker = true,
        filterResourcesPredicate = () => true
    } = props;

    const { localize } = useLocalization();
    const resourceTracker = useProgressTracker();
    const [file, setFile] = React.useState<File>();
    const { dispatcher, rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const [isFileAboveLimit, setIsFileAboveLimit] = React.useState<boolean>(false);
    const [isVersionsModalHidden, setIsVersionsModalHidden] = React.useState<boolean>(true);
    const [isSampleSelectedFirstTime, setIsSampleSelectedFirstTime] = React.useState<boolean>(false);
    const [selectedSampleKey, setSelectedSampleKey] = React.useState<string>(selectedSampleTitle);
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });
    const [selectedLanguageOptionKey, setSelectedLanguageOptionKey] = React.useState<string>(selectedLanguage);
    const { isLargeOrGreater } = useResponsiveMode();

    const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);

    React.useEffect(() => {
        if (selectedSampleTitle !== selectedSampleKey) {
            handleSampleClick(samplesList.find(sample => sample.title === selectedSampleTitle));
        }
    }, [selectedSampleTitle]);

    React.useEffect(() => {
        if (!isSignedIn && samplesList?.length && !isSampleSelectedFirstTime) {
            handleSampleClick(samplesList[0]);
            setIsSampleSelectedFirstTime(true);
        }
    }, [isSignedIn, samplesList]);

    React.useEffect(() => {
        if (isSignedIn && subscriptionId && displayAzureResourcePicker) {
            dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
                id: subscriptionId,
                localStoreHouse: resourcesStoreHouse,
                filterPredicate: (account: AzureCsAccountStore) => account.sku.code === 'F0' || account.sku.code === 'S'
            });
        }
    }, [subscriptionId]);

    React.useEffect(() => {
        switch (acceptedResourceType) {
            case 'TextAnalytics':
                setIsValidResource(
                    validateLanguageStudioResource({
                        localize,
                        isPaidDemo,
                        supportedRegions,
                        resource: csAccountStore,
                        regionsLink: supportedRegionLink
                    })
                );
                break;
            case 'TextTranslation':
                setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
                break;
            default:
        }
    }, [csAccountStore?.id]);

    React.useEffect(() => {
        if (selectedLanguage !== selectedLanguageOptionKey) {
            setSelectedLanguageOptionKey(selectedLanguage);
        }
    }, [selectedLanguage]);

    useAsyncEffect(async () => {
        if (file) {
            const uploadedDocument = await fileService.readFromDisk(file);
            onDocumentChange(uploadedDocument.slice(0, documentLimit));
            setIsFileAboveLimit(uploadedDocument.length > documentLimit);
        }
    }, [file]);

    const modelVersionOptions: IDropdownOption[] = React.useMemo(() => {
        if (supportedModelVersionOptions) {
            return supportedModelVersionOptions;
        }
        const options: IDropdownOption<any>[] = [
            {
                key: 'GA',
                text: localize(messages.generallyAvailable),
                data: {
                    version: LuisConstants.Demos.ModelVersions.GA,
                    secondaryText: `${localize(messages.model)}: ${LuisConstants.Demos.ModelVersions.GA}`
                }
            }
        ];

        if (LuisConstants.Demos.ModelVersions.Preview) {
            options.push({
                key: 'Preview',
                text: localize(messages.preview),
                data: {
                    version: LuisConstants.Demos.ModelVersions.Preview,
                    secondaryText: `${localize(messages.model)}: ${LuisConstants.Demos.ModelVersions.Preview}`
                }
            });
        }
        return options;
    }, []);

    let languageOptions: IDropdownOption[] = [];
    const autoLanguageDetectionItemKey = 'autoLanguageDetectionItemKey';
    const languageResourcesOptions: IDropdownOption[] = resourcesStoreHouse.items
        .filter(filterResourcesPredicate)
        .map(r => ({ data: r, key: r.id, text: r.name }));

    if (onLanguageAutoDetectionClick) {
        languageOptions.push({ key: autoLanguageDetectionItemKey, text: localize(messages.autoDetectLanguageItem) });
        languageOptions.push({ key: 'divider', text: '', itemType: DropdownMenuItemType.Divider });
    }

    languageOptions = [
        ...languageOptions,
        ...orderBy(
            languagesList.map(l => ({ key: l.code, text: l.name })),
            'text'
        )
    ];

    const handleLanguageChange = (optionKey: string) => {
        if (optionKey === autoLanguageDetectionItemKey) {
            setSelectedLanguageOptionKey(autoLanguageDetectionItemKey);
            onLanguageAutoDetectionClick(true);
        } else {
            setSelectedLanguageOptionKey(optionKey as Language['code']);
            onLanguageChange?.(optionKey as Language['code']);

            if (onLanguageAutoDetectionClick) {
                onLanguageAutoDetectionClick(false);
            }
        }
        const changedLanguage = languagesList.find(lan => lan.code === optionKey);
        logFeaturesTextLanguage(featureName, changedLanguage ? changedLanguage.name : '');
    };

    const handleModelVersionChange = (optionKey: TryoutModelVersion) => {
        onModelVersionChange?.(optionKey);
    };

    const handleApiVersionChange = (optionKey: TryoutModelVersion) => {
        onApiVersionChange?.(optionKey);
    };

    const handleSampleClick = (sample: DemoSample = null) => {
        if (!sample) {
            setSelectedSampleKey(null);
        } else {
            setSelectedSampleKey(sample.title);
        }

        onSampleClick?.(sample);
        onDocumentChange(sample?.document ?? '');

        if (!sample) {
            return;
        }

        if (sample.language) {
            handleLanguageChange(sample.language);
        }

        logSampleClick(featureName, sample.title);
        setIsFileAboveLimit(false);
    };

    const handleClearInputClick = () => {
        onInputClearClick?.();
        onSampleClick?.(null);
        onDocumentChange('');
        setIsFileAboveLimit(false);
    };

    const handleDragAndDrop = (files: any) => {
        setFile(files?.[0]);

        logDragAndDropFiles(featureName);
    };

    const getOptionVersionWithKey = React.useCallback(
        (key: TryoutModelVersion, versionType: 'api' | 'model') => {
            switch (versionType) {
                case 'api':
                    return supportedApiVersionOptions.find(i => i.key === key).data.version;
                case 'model':
                    return modelVersionOptions.find(i => i.key === key)?.data?.version;
                default:
                    return null;
            }
        },
        [supportedApiVersionOptions, modelVersionOptions]
    );

    const versionsOptionsModal = () => (
        <Stack styles={{ root: { alignItems: 'center' } }} horizontal tokens={{ childrenGap: 5 }}>
            {selectedModelVersion && (
                <LuisText
                    styles={{
                        root: {
                            paddingRight: '5px',
                            borderRight: `2px solid ${stepTheme.separatorColor}`
                        }
                    }}
                    fontWeight={400}>
                    {`${localize(messages.modelVersion)}: ${getOptionVersionWithKey(selectedModelVersion, 'model')}`}
                </LuisText>
            )}
            {selectedApiVersion && (
                <LuisText
                    styles={{
                        root: {
                            paddingRight: '5px',
                            borderRight: `2px solid ${stepTheme.separatorColor}`
                        }
                    }}
                    fontWeight={400}>
                    {`${localize(messages.apiVersion)}: ${getOptionVersionWithKey(selectedApiVersion, 'api')}`}
                </LuisText>
            )}
            {isSignedIn && (
                <Link styles={{ root: { fontSize: '14px' } }} onClick={() => setIsVersionsModalHidden(false)}>
                    {localize(messages.changeVersions)}
                </Link>
            )}
        </Stack>
    );

    const defaultOptionsRenderer = isSignedIn
        ? () => (
              <Stack tokens={{ childrenGap: 16 }}>
                  {!disableModelVersionSelection && (
                      <StyledDropdown
                          aria-required
                          isLargeOrGreater={isLargeOrGreater}
                          maxHeight={300}
                          orderByKey="none"
                          isSearchEnabled={false}
                          options={modelVersionOptions}
                          disabled={!isValidResource.isValid}
                          data-automation-id="selectedVersionDropdown"
                          selectedKey={selectedModelVersion}
                          ariaLabel={localize(messages.selectModelVersionPlaceholder)}
                          placeholder={localize(messages.selectModelVersionPlaceholder)}
                          onChange={(_, option) => handleModelVersionChange(option.key as TryoutModelVersion)}
                          onRenderLabel={() =>
                              renderDescribedLabel({
                                  label: localize(messages.selectModelVersionLabel),
                                  descriptionIconProps: {
                                      isDarkMode: false,
                                      content: localize(messages.selectModelVersionDescription),
                                      ariaLabel: localize(messages.selectModelVersionDescription)
                                  }
                              })
                          }
                      />
                  )}

                  {(!disableLanguageSelection || displayAzureResourcePicker) && (
                      <StyledStack isLargeOrGreater horizontal={isLargeOrGreater} tokens={{ childrenGap: 16 }}>
                          {!disableLanguageSelection && (
                              <StyledDropdown
                                  aria-required
                                  isLargeOrGreater={isLargeOrGreater}
                                  maxHeight={300}
                                  orderByKey="none"
                                  options={languageOptions}
                                  disabled={!isValidResource.isValid}
                                  data-automation-id="languageDropdown"
                                  selectedKey={selectedLanguageOptionKey}
                                  ariaLabel={localize(messages.selectLanguagePlaceholder)}
                                  placeholder={localize(messages.selectLanguagePlaceholder)}
                                  onChange={(_, option) => handleLanguageChange(option.key as string)}
                                  onRenderLabel={() =>
                                      renderDescribedLabel({
                                          label: localize(messages.selectLanguageLabel),
                                          descriptionIconProps: {
                                              isDarkMode: false,
                                              content: localize(messages.selectLanguageDescription),
                                              ariaLabel: localize(messages.selectLanguageDescription)
                                          }
                                      })
                                  }
                              />
                          )}

                          {displayAzureResourcePicker && (
                              <StyledDropdown
                                  aria-required
                                  isLargeOrGreater={isLargeOrGreater}
                                  selectedKey={csAccountStore?.id}
                                  options={languageResourcesOptions}
                                  data-automation-id="azureResourceDropdown"
                                  errorMessage={isValidResource.secondaryErrorMessage}
                                  placeholder={localize(messages.selectResourcePlaceholder)}
                                  ariaLabel={localize(messages.selectResourceLabel)}
                                  isLoading={resourcesStoreHouse.tracker.isRunning || resourceTracker.isRunning}
                                  onRenderOption={p => (
                                      <Stack horizontal verticalFill verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                          <LuisText
                                              color={stepTheme.defaultTextColor}
                                              fontWeight={p.itemType === DropdownMenuItemType.Header && 500}>
                                              {p.text}
                                          </LuisText>
                                          {p.data && (
                                              <SecondaryText variant="xSmall">
                                                  ({(p.data as AzureCsAccountStore)?.region}, {(p.data as AzureCsAccountStore)?.sku.code})
                                              </SecondaryText>
                                          )}
                                      </Stack>
                                  )}
                                  onRenderLabel={() =>
                                      renderDescribedLabel({
                                          label: resourceDropdownLabel ?? localize(messages.selectResourceLabel),
                                          descriptionIconProps: { isDarkMode: false, content: localize(messages.selectResourceDescription) }
                                      })
                                  }
                                  onChange={(_, option) => {
                                      logFeaturesResouceSelection(featureName, option.key as string, '');
                                      dispatcher.dispatch('switchAzureCsAccountInSession', {
                                          tracker: resourceTracker,
                                          csAccountStore: resourcesStoreHouse.items.find(r => r.id === option.key)
                                      });
                                  }}
                              />
                          )}
                      </StyledStack>
                  )}
              </Stack>
          )
        : null;

    return (
        <Stack {...containerStackProps} id="demoPageDocumentInputStep" tokens={{ childrenGap: 25, ...containerStackProps?.tokens }}>
            {isSignedIn && !isValidResource.isValid && (
                <MessageBar messageBarType={MessageBarType.blocked} styles={{ innerText: { width: '100%' } }}>
                    <Stack>
                        {isValidResource.mainErrorMessage}
                        <DefaultButton
                            styles={{ root: { alignSelf: 'flex-end' } }}
                            onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                            {localize(messages.goToSettings)}
                        </DefaultButton>
                    </Stack>
                </MessageBar>
            )}

            {displayTitle && (
                <LuisText as="h2" variant="xLarge" fontWeight={600}>
                    {localize(isSignedIn ? messages.enterTextStepTitle : messages.signedOutEnterSourceText)}
                </LuisText>
            )}

            {onRenderOptions ? onRenderOptions(defaultOptionsRenderer) : defaultOptionsRenderer?.()}

            {!disableVersionsModalSelection && versionsOptionsModal()}

            <Stack grow horizontal={isLargeOrGreater} tokens={{ childrenGap: 24 }}>
                <StyledStackText isLargeOrGreater={isLargeOrGreater} haveHorizontalOptions={Boolean(onRenderHorizontalOptions)} grow>
                    <LuisTextField
                        multiline
                        value={document}
                        resizable={false}
                        readOnly={!isSignedIn}
                        data-automation-id="sourceTextInput"
                        disabled={isSignedIn && !isValidResource.isValid}
                        placeholder={localize(messages.sourceTextPlaceholder)}
                        label={isSignedIn && localize(messages.enterSourceText)}
                        onChange={(_, newValue) => onDocumentChange(newValue.slice(0, documentLimit))}
                        styles={mergeStyleSets(
                            { root: { direction: isTextRTL ? 'rtl' : 'ltr' }, fieldGroup: { minHeight: '370px' } },
                            textFieldProps?.styles
                        )}
                        onRenderDescription={() => (
                            <Stack horizontalAlign="end">
                                <LuisText variant="smallPlus" color={stepTheme.secondaryTextColor}>
                                    {document.length}/{documentLimit}
                                </LuisText>
                            </Stack>
                        )}
                        onRenderLabel={p => (
                            <Stack
                                horizontal
                                verticalAlign="end"
                                horizontalAlign="space-between"
                                tokens={{ padding: '0 0 5px 0' }}
                                styles={{ root: { direction: 'ltr' } }}>
                                <LuisText fontWeight={500}>{p.label}</LuisText>
                                {isSignedIn && (
                                    <ActionButton
                                        styles={actionButtonStyles}
                                        onClick={handleClearInputClick}
                                        iconProps={{ iconName: 'Reset' }}
                                        text={localize(messages.clearText)}
                                        disabled={!document.length || !isValidResource.isValid}
                                    />
                                )}
                            </Stack>
                        )}
                        {...textFieldProps}
                    />

                    <Stack wrap horizontal tokens={{ childrenGap: 10 }}>
                        {isSignedIn && !hideFilesUploader && (
                            <Stack tokens={{ childrenGap: 3 }}>
                                {isFileAboveLimit && (
                                    <MessageBar messageBarType={MessageBarType.warning}>
                                        {localize(messages.uploadingLargeFilesNote)}
                                    </MessageBar>
                                )}

                                <DragAndDropInput
                                    showUploadedFiles
                                    isMultiple={false}
                                    disabled={!isValidResource.isValid}
                                    onDropFiles={files => handleDragAndDrop(files)}
                                />
                            </Stack>
                        )}

                        {samplesList.map(sample => (
                            <SampleButton
                                key={getId()}
                                sampleTitle={sample.title}
                                sampleBody={sample.document}
                                data-automation-id="sampleInputButton"
                                onClick={() => handleSampleClick(sample)}
                                disabled={isSignedIn && !isValidResource.isValid}
                                checked={selectedSampleKey === sample.title}
                            />
                        ))}
                    </Stack>
                </StyledStackText>

                {onRenderHorizontalOptions && <Separator vertical />}

                {onRenderHorizontalOptions && onRenderHorizontalOptions(!isValidResource.isValid)}
            </Stack>

            <ApiAndModelVersionsModal
                modelVersionOptions={modelVersionOptions}
                apiVersionOptions={supportedApiVersionOptions}
                learnMoreLink={versionsLearnMoreLink}
                onDismiss={() => setIsVersionsModalHidden(!isVersionsModalHidden)}
                hidden={isVersionsModalHidden}
                handleApiVersionChange={handleApiVersionChange}
                handleModelVersionChange={handleModelVersionChange}
                apiVersion={selectedApiVersion}
                modelVersion={selectedModelVersion}
            />

            {children}
        </Stack>
    );
});
